import { isEqual, keyBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import ListMatchTableRow from 'dashboard/components/ListMatches/ListMatchTableRow';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import TableLayout, { TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { ModuleListMatches } from 'dashboard/modules/ListMatches';
import Placeholder from 'dashboard/modules/Placeholder';
import Card from 'design-system/components/Card';

interface ListMatchesFilterParams {
  listType: List['type'][];
}

interface ListMatchesProps {
  module: ModuleListMatches;
  lists: List[];
  listItems: ListItem[];
  listMatches: ListMatch[];
  isLoading: boolean;
}

const initialListMatchesFilters: ListMatchesFilterParams = {
  listType: [],
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ListMatches = ({ module, lists, listItems, listMatches, isLoading }: ListMatchesProps) => {
  const listsMap = keyBy('id', lists);
  const listItemsMap = keyBy('id', listItems);

  const [filters, setFilters] = useState<ListMatchesFilterParams>(initialListMatchesFilters);

  const filteredListMatches = useMemo(() => {
    return listMatches.filter((listMatch) => {
      const list = listsMap[listMatch.relationships.list.data.id];
      return filters.listType.length === 0 || filters.listType.includes(list.type);
    });
  }, [filters, listMatches, listsMap]);

  const listMatchesRows = filteredListMatches.map((lm) => (
    <ListMatchTableRow
      list={listsMap[lm.relationships.list.data.id]}
      listItem={listItemsMap[lm.relationships['list-item'].data.id]}
      listMatch={lm}
      key={`${module.token}-${lm.id}`}
    />
  ));

  const handleUpdateFilters = (values: List['type'][]) => {
    setFilters({ ...filters, listType: values });
  };

  const FILTER_LABEL_TEXT_MAPPER = (values: string[]) => {
    if (values.length === 1) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return LIST_TYPE_FILTER_OPTIONS.find((v) => v.value === values[0]).label;
    } else if (values.length === LIST_TYPE_FILTER_OPTIONS.length) {
      return 'All types';
    } else if (values.length > 1) {
      return `${values.length} types`;
    } else {
      return 'All types';
    }
  };

  const LIST_TYPE_FILTER_OPTIONS = Object.keys(LIST_TYPE_NAMES).map(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (listType: keyof typeof LIST_TYPE_NAMES) => ({
      label: LIST_TYPE_NAMES[listType],
      value: listType,
    }),
  );

  const hasUsedFilters = !isEqual(filters, initialListMatchesFilters);

  if (isLoading) {
    return <Placeholder title='List matches' loading={true} />;
  }

  if (listMatches.length === 0) {
    return <Placeholder title='List matches' message='There are no list matches.' />;
  }

  return (
    <Card data-test={`list-matches-${module.token}`}>
      <Card.Body>
        <Header>
          <Card.Title>List matches</Card.Title>
          <MultiSelect
            value={filters.listType}
            selectOptionValues={LIST_TYPE_FILTER_OPTIONS}
            labelTextMapper={FILTER_LABEL_TEXT_MAPPER}
            onChange={handleUpdateFilters}
            data-test={`list-matches-${module.token}__list-type-select`}
          />
        </Header>
        <TableLayout
          dataTest={`list-matches-${module.token}__table`}
          isEmpty={listMatchesRows.length === 0}
          isLoading={false}
          showHeader={listMatchesRows.length > 0}
          headerTR={
            <>
              <TableTH>List name</TableTH>
              <TableTH>List type</TableTH>
              <TableTH>Matched item</TableTH>
              <TableTH>Source</TableTH>
            </>
          }
          nullState={
            <TableNullState
              hasUsedFilters={hasUsedFilters}
              dataTestBlock={`list-matches-${module.token}__table-null-state`}
            >
              There are no list matches.
            </TableNullState>
          }
        >
          {listMatchesRows}
        </TableLayout>
      </Card.Body>
    </Card>
  );
};

export default ListMatches;
